import { CheckCircle, Edit, MoreVertical, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type {
  LienholderType,
  LienStatus,
  LienType,
  ReadLien,
} from '@/services/api/ledgerService';

import type { TableColumn } from '../../common/DataTable/types';
import { formatCurrency } from '../shared/formatUtils';

/**
 * Get the display name for the lienholder.
 */
const getLienholderName = (lien: ReadLien): string => {
  if (lien.lienholder_type === 'PROVIDER' && lien.lienholder_provider) {
    return lien.lienholder_provider.name;
  }
  if (lien.lienholder_type === 'COVERAGE' && lien.lienholder_coverage) {
    return lien.lienholder_coverage.carrier_name || 'Unknown Coverage';
  }
  return 'Unknown';
};

/**
 * Lienholder type badge configuration.
 */
const LIENHOLDER_TYPE_CONFIG: Record<
  LienholderType,
  { label: string; className: string }
> = {
  PROVIDER: {
    label: 'Provider',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  COVERAGE: {
    label: 'Coverage',
    className: 'bg-purple-100 text-purple-800 border-purple-200',
  },
};

/**
 * Lien type badge configuration.
 */
const LIEN_TYPE_CONFIG: Record<LienType, { label: string; className: string }> =
  {
    STATUTORY: {
      label: 'Statutory',
      className: 'bg-amber-100 text-amber-800 border-amber-200',
    },
    CONTRACTUAL: {
      label: 'Contractual',
      className: 'bg-gray-100 text-gray-800 border-gray-200',
    },
  };

/**
 * Lien status badge configuration.
 */
const LIEN_STATUS_CONFIG: Record<
  LienStatus,
  { label: string; className: string }
> = {
  ACTIVE: {
    label: 'Active',
    className: 'bg-green-100 text-green-800 border-green-200',
  },
  RESOLVED: {
    label: 'Resolved',
    className: 'bg-gray-100 text-gray-500 border-gray-200',
  },
};

interface LiensColumnsOptions {
  onEdit: (lien: ReadLien) => void;
  onResolve: (lien: ReadLien) => void;
  onDelete: (lien: ReadLien) => void;
}

/**
 * Column definitions for the liens table.
 * Uses the DataTable component pattern from the codebase.
 */
export const createLiensColumns = ({
  onEdit,
  onResolve,
  onDelete,
}: LiensColumnsOptions): TableColumn<ReadLien>[] => [
  {
    id: 'lienholder_name',
    header: 'Lienholder',
    accessorFn: getLienholderName,
    cell: (row) => {
      const name = getLienholderName(row);
      return (
        <span className="font-medium text-zinc-900 dark:text-white">
          {name}
        </span>
      );
    },
    sortable: true,
  },
  {
    id: 'lienholder_type',
    header: 'Lienholder Type',
    accessorKey: 'lienholder_type',
    width: '140px',
    cell: (row) => {
      const config = LIENHOLDER_TYPE_CONFIG[row.lienholder_type];
      return (
        <Badge
          variant="outline"
          className={cn('font-medium', config.className)}
        >
          {config.label}
        </Badge>
      );
    },
  },
  {
    id: 'lien_type',
    header: 'Lien Type',
    accessorKey: 'lien_type',
    width: '130px',
    cell: (row) => {
      const config = LIEN_TYPE_CONFIG[row.lien_type];
      return (
        <Badge
          variant="outline"
          className={cn('font-medium', config.className)}
        >
          {config.label}
        </Badge>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    width: '110px',
    cell: (row) => {
      const config = LIEN_STATUS_CONFIG[row.status];
      return (
        <Badge
          variant="outline"
          className={cn('font-medium', config.className)}
        >
          {config.label}
        </Badge>
      );
    },
  },
  {
    id: 'exposure_amount',
    header: 'Exposure',
    accessorKey: 'exposure_amount',
    width: '130px',
    align: 'right',
    cell: (row) => {
      const amount = formatCurrency(row.exposure_amount);
      return (
        <span className="font-medium text-zinc-900 dark:text-white">
          {amount}
        </span>
      );
    },
    sortable: true,
  },
  {
    id: 'actions',
    header: '',
    width: '60px',
    align: 'center',
    cell: (row) => {
      const isResolved = row.status === 'RESOLVED';

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            {!isResolved && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onResolve(row);
                }}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Resolve
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
              }}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
